import { useEffect, useRef, useState } from 'react'
import { ChevronDown, HelpCircle, PlayCircle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { useHelp } from './help-provider'
import { getHelpContent } from './registry'

/**
 * Per-section Help entry point. Drop `<SectionHelp sectionKey="leads" />` into
 * a page's PageHeader `actions`. It renders a Help button that opens a Sheet
 * explaining every part of the section, and on first visit it auto-starts that
 * section's walkthrough exactly once. The panel also has a Replay button.
 */
export function SectionHelp({ sectionKey }: { readonly sectionKey: string }) {
  const content = getHelpContent(sectionKey)
  const { isFirstRun, markSeen, startTour } = useHelp()
  const [open, setOpen] = useState(false)
  const autoStarted = useRef(false)

  useEffect(() => {
    if (autoStarted.current || !content) return
    if (isFirstRun(sectionKey)) {
      autoStarted.current = true
      markSeen(sectionKey)
      startTour(sectionKey)
    }
    // Run once per mount for this section; isFirstRun/markSeen are stable.
  }, [sectionKey])

  if (!content) return null

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-1.5"
        aria-label={`Help for ${content.title}`}
      >
        <HelpCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Help</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{content.title}</SheetTitle>
            <SheetDescription>{content.intro}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-2">
            {content.panel.map((section, idx) => (
              <details
                key={section.heading}
                open={idx === 0}
                className="group rounded-lg border border-[rgba(255,255,255,0.08)] bg-[var(--color-surface-raised)]/40 px-3.5 py-2.5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[var(--color-silver-bright)] [&::-webkit-details-marker]:hidden">
                  {section.heading}
                  <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-silver)] transition-transform group-open:rotate-180" />
                </summary>
                <ul className="mt-2.5 space-y-2 border-t border-[rgba(255,255,255,0.06)] pt-2.5">
                  {section.items.map((item, i) => (
                    <li
                      key={i}
                      className="text-sm leading-relaxed text-[var(--color-silver)]"
                    >
                      {item.term && (
                        <span className="font-medium text-[var(--color-silver-bright)]">
                          {item.term}{' '}
                        </span>
                      )}
                      {item.text}
                    </li>
                  ))}
                </ul>
              </details>
            ))}

            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  setOpen(false)
                  startTour(sectionKey)
                }}
              >
                <PlayCircle className="h-4 w-4" />
                Replay walkthrough
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
